import { useEffect, useMemo, useRef, useState } from 'react';
import { Circle } from '@phosphor-icons/react';
import styled from 'styled-components';
import { Form, FormGroup } from 'semantic-ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { SkupremeModal } from '../../../components';
import {
  BoldText,
  ColoredPill,
  DropdownFormField,
  HoverBorderButton,
  MuteText,
  NumberFormField,
  StyledButton,
} from '../../../elements';
import { PackagingSearchInput } from './package-search';
import {
  CommonUtility,
  ShippingSpeedOptions,
  theme,
  ToastMessage,
} from '../../../utility';
import { PackagingService } from '../../../utility/services/packaging';
import { ConversionUtility } from '../../../utility/conversions';
import { buildPackageItems } from '../../../utility/packaging/package-items';
import { sumContentsWeightInPounds } from '../../../utility/packaging/packed-weight';

const GreyBox = styled.div`
  background: ${({ theme }) => theme.colors.box};
  padding: 10px;
  border-radius: 5px;
  margin: 10px 0;
`;

const RateContainer = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.box};
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 5px;
  height: 100%;
  width: 100%;
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.colors.white};

  &:hover {
    border: 2px solid ${({ theme }) => theme.colors.grey};
  }

  &.selected {
    border: 2px solid ${({ theme }) => theme.colors.primary};
    box-sizing: border-box;
  }
`;

const PackagingSchema = yup
  .object()
  .shape({
    orderSpeed: yup.string().required('Speed is required'),
    ounces: yup.string().nullable(),
    pounds: yup.string().nullable(),
  })
  .test('weight-required', 'Either Ounces or Pounds is required', function (values) {
    if (!isNaN(values.ounces) && !isNaN(values.pounds)) {
      if (Number(values.ounces) > 0 || Number(values.pounds) > 0) {
        return true;
      }
    }
    return this.createError({
      path: 'pounds',
      message: 'Either Ounces or Pounds is required',
    });
  });

export function CreateNewLabelPopup({
  warehouseId,
  orderToUse,
  open,
  items,
  onClose,
  labelForPackage,
  refresh,
  data,
  onLabelGenerated,
  labelAllocations,
}) {
  const [packagingMaterial, setPackagingMaterial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState();
  const [selectedRate, setSelectedRate] = useState(-1);
  const rateRequestGenerationRef = useRef(0);
  const {
    formState: { errors },
    control,
    watch,
    setValue,
    handleSubmit,
    getValues,
    reset,
  } = useForm({
    resolver: yupResolver(PackagingSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) {
      reset({
        orderSpeed: 'Standard',
        ounces: '',
        pounds: '',
      });
    }
  }, [open]);

  useEffect(() => {
    if (!CommonUtility.isValidArray(items)) return;

    const entries =
      labelForPackage !== null
        ? items
            .filter(item => item.qty)
            .map(item => ({
              weight: data?.contents?.find(x => x.productId._id === item.item_id)
                ?.productId?.package_dimensions?.weight,
              qty: item.qty,
            }))
        : items.map(packItem => ({
            weight: packItem.productId.package_dimensions?.weight,
          }));

    const convertedWeight = ConversionUtility.convertToPoundsAndOunces(
      sumContentsWeightInPounds(entries),
    );
    setValue('pounds', convertedWeight.pounds);
    setValue('ounces', Math.round(convertedWeight.ounces));
  }, [items, labelForPackage]);

  const ounces = watch('ounces');
  const pounds = watch('pounds');
  const orderSpeed = watch('orderSpeed');

  // TODO: zeroing the ounces drops the remainder (20 oz becomes 1 lb 0 oz). Should carry oz % 16.
  // Same copy exists in six label/rate forms; extract one helper.
  useEffect(() => {
    if (ounces > 15) {
      setValue('ounces', 0);
      setValue('pounds', +getValues('pounds') + 1);
    }
  }, [ounces]);

  useEffect(() => {
    rateRequestGenerationRef.current += 1;
    setRates(null);
    setSelectedRate(-1);
    setLoading(false);
  }, [pounds, ounces, orderSpeed, packagingMaterial?._id]);

  const getRates = async formValues => {
    if (!packagingMaterial) {
      ToastMessage.error('Please select a Packaging Material');
      return;
    }

    let packageItems;
    try {
      packageItems = buildPackageItems(
        items,
        labelForPackage,
        labelAllocations,
        orderToUse?.items || [],
      );
    } catch (error) {
      ToastMessage.error(error.message);
      return;
    }

    const payload = {
      orderId: orderToUse?._id,
      fulfillmentProviderId: orderToUse?.fulfillment_provider_id,
      orderSpeed: formValues.orderSpeed,
      packages: [
        {
          packagingMaterialId: packagingMaterial?._id,
          pounds: Number(formValues.pounds || 0),
          ounces: Number(formValues.ounces || 0),
          items: packageItems,
        },
      ],
    };

    const requestGeneration = (rateRequestGenerationRef.current += 1);

    try {
      setLoading(true);
      const res = await PackagingService.shippingRates(payload);
      if (requestGeneration === rateRequestGenerationRef.current) {
        setRates(res);
      }
    } catch (error) {
      if (requestGeneration === rateRequestGenerationRef.current) {
        ToastMessage.apiError(error);
      }
    } finally {
      if (requestGeneration === rateRequestGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const generateLabel = async formValues => {
    const selectedShippingRate = filteredRates?.[selectedRate];

    if (!packagingMaterial || selectedRate === -1 || !selectedShippingRate) {
      ToastMessage.error('No Rate Selected');
      return;
    }

    let packageItems;
    try {
      packageItems = buildPackageItems(
        items,
        labelForPackage,
        labelAllocations,
        orderToUse?.items || [],
      );
    } catch (error) {
      ToastMessage.error(error.message);
      return;
    }

    const payload = {
      orderId: orderToUse?._id,
      fulfillmentProviderId: orderToUse?.fulfillment_provider_id,
      orderSpeed: formValues.orderSpeed,
      toteId: data?._id,
      force: 'true',
      mode: CommonUtility.isStagingEnv() ? 'test' : 'production',
      allocationIds: labelAllocations?.map(x => x.allocationId?._id),
      packages: [
        {
          packagingMaterialId: packagingMaterial?._id,
          pounds: Number(formValues.pounds || 0),
          ounces: Number(formValues.ounces || 0),
          provider: selectedShippingRate.provider.value,
          service: selectedShippingRate.service.value,
          package: selectedShippingRate.package.value,
          items: packageItems,
        },
      ],
    };

    try {
      setLoading(true);
      const { data } = await PackagingService.createLabel(payload);
      ToastMessage.success('Label generated successfully');
      if (labelForPackage !== null) {
        onLabelGenerated(labelForPackage, data);
      } else {
        refresh();
      }
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const packageRate = useMemo(() => {
    if (CommonUtility.isValidObject(rates?.packages?.[0])) {
      return rates?.packages?.[0];
    }
    return null;
  }, [rates]);

  const filteredRates = useMemo(
    () => packageRate?.rates?.filter(x => !x.error),
    [packageRate],
  );

  useEffect(() => {
    if (CommonUtility.isValidArray(filteredRates)) {
      let min = Infinity;
      let indexToSelect = -1;
      filteredRates.forEach((rate, i) => {
        if (rate.rate < min) {
          min = rate.rate;
          indexToSelect = i;
        }
      });
      setSelectedRate(indexToSelect);
    }
  }, [filteredRates]);

  return (
    <SkupremeModal
      title={
        labelForPackage !== null
          ? `Create Label for Package ${labelForPackage + 1}`
          : 'Create Label'
      }
      size="tiny"
      open={open}
      onClose={onClose}
    >
      <div className="col">
        <MuteText>Select Packaging Material</MuteText>
        <PackagingSearchInput
          inputClassName="w-100"
          warehouseId={warehouseId}
          onResultClick={setPackagingMaterial}
        />
      </div>
      {CommonUtility.isValidObject(packagingMaterial) && (
        <GreyBox>
          <BoldText>Package Selected</BoldText>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <span title={packagingMaterial.name} className="mr-2">
                {packagingMaterial.name}
              </span>
              <ColoredPill
                color={
                  packagingMaterial.status === 'active'
                    ? theme.colors.green
                    : theme.colors.grey
                }
              >
                <Circle weight="fill" />
                {CommonUtility.toTitleCase(packagingMaterial.status)}
              </ColoredPill>
            </div>
            <div className="text-nowrap">
              <span>
                {CommonUtility.roundNumber(packagingMaterial.dimensions?.length)}
              </span>
              <MuteText className="mx-1">x</MuteText>
              <span>
                {CommonUtility.roundNumber(packagingMaterial.dimensions?.height)}
              </span>
              <MuteText className="mx-1">x</MuteText>
              <span>
                {CommonUtility.roundNumber(packagingMaterial.dimensions?.width)}
              </span>
              <MuteText className="ml-1">{packagingMaterial.dimensions?.uom}.</MuteText>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex">
              <MuteText>Packaing Type:</MuteText>
              <span className="ml-2">{packagingMaterial.packagingType}</span>
            </div>
            <div className="text-nowrap">
              <MuteText>Available:</MuteText>
              <span className="ml-2 mr-1">{packagingMaterial.unitsAvailable}</span>
              <MuteText>Units</MuteText>
            </div>
          </div>
        </GreyBox>
      )}
      <Form
        className="mt-4"
        onSubmit={
          selectedRate === -1 ? handleSubmit(getRates) : handleSubmit(generateLabel)
        }
      >
        <DropdownFormField
          name="orderSpeed"
          label="Speed"
          control={control}
          error={errors?.orderSpeed}
          hint={errors?.orderSpeed?.message}
          options={ShippingSpeedOptions}
        />
        <FormGroup widths="equal" className="mb-0">
          <NumberFormField
            name="pounds"
            label="Pounds"
            control={control}
            error={errors?.pounds}
            hint={errors?.pounds?.message}
          />
          <NumberFormField
            name="ounces"
            label="Ounces"
            control={control}
            error={errors?.ounces}
            hint={errors?.ounces?.message}
          />
        </FormGroup>
        <div className="row">
          {filteredRates?.map((rate, i) => (
            <div className="col-6 mt-2 pl-0 pr-0" onClick={() => setSelectedRate(i)}>
              <RateContainer
                className={`${selectedRate === i ? 'selected' : ''} ${
                  i % 2 === 0 ? '' : 'ml-2'
                }`}
              >
                <div className="d-flex flex-column">
                  <BoldText>{rate?.provider?.key}</BoldText>
                  <MuteText>{rate.service?.key}</MuteText>
                </div>
                <span>{CommonUtility.currencyFormat(rate.rate)}</span>
              </RateContainer>
            </div>
          ))}
        </div>
        <div className="mt-4 d-flex justify-content-end">
          <HoverBorderButton disabled={loading} onClick={onClose} type="button">
            Close
          </HoverBorderButton>
          {selectedRate === -1 ? (
            <StyledButton loading={loading} type="submit" className="ml-2">
              Get Rates
            </StyledButton>
          ) : (
            <StyledButton loading={loading} type="submit" className="ml-2">
              Generate Label
            </StyledButton>
          )}
        </div>
      </Form>
    </SkupremeModal>
  );
}
